import './PasswordField.scss';
import '../../PermissionsList/ShareLink/ShareLink.scss';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter/PasswordStrengthMeter';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { Icon, IIconProps } from 'office-ui-fabric-react/lib/Icon';
import stringsResx from '../../Share.resx';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as React from 'react';

// Features
const PasswordStrengthMeterUI = { ODB: 1204 };

export interface IPasswordFieldState {
    currentPassword: string;
    disablePasswordField: boolean;
    displayRemovePasswordButton: boolean;
    displayNotificationText: boolean;
    hasExistingPassword: boolean;
}

export interface IPasswordFieldProps {
    onPasswordChange: (newPassword: string) => void;
    detailsPane?: boolean;
    lastModifiedBy?: string; // Only used from the password details pane to manually pass in the last set by name
    readOnly?: boolean;
    showGleam?: boolean;
    tabIndex?: number;
}

export class PasswordField extends React.Component<IPasswordFieldProps, IPasswordFieldState> {
    private _currentStrengthMeterValue: string = '';
    constructor(props: IPasswordFieldProps, context: any) {
        super(props);
        if (this.props.detailsPane) {
            let defaultPassword = '************';
            this.state = {
                currentPassword: defaultPassword,
                disablePasswordField: true,
                displayRemovePasswordButton: true,
                displayNotificationText: true,
                hasExistingPassword: true
            };
        } else {
            this.state = { // No link, or not password protected
                ...this.state,
                currentPassword: '',
                disablePasswordField: false,
                displayRemovePasswordButton: false,
                displayNotificationText: false,
                hasExistingPassword: false
            };
        }
        this._renderShareReminder = this._renderShareReminder.bind(this);
        this._onFocusPasswordFieldDetails = this._onFocusPasswordFieldDetails.bind(this);
        this._onFocusPasswordField = this._onFocusPasswordField.bind(this);
        this._onBlurPasswordField = this._onBlurPasswordField.bind(this);
        this._onUpdatePassword = this._onUpdatePassword.bind(this);
        this._onDeletePassword = this._onDeletePassword.bind(this);
        this._getPasswordFieldAriaLabel = this._getPasswordFieldAriaLabel.bind(this);
        this._getStrengthMeterValue = this._getStrengthMeterValue.bind(this);
    }

    public render(): React.ReactElement<{}> {
        let focusPasswordFieldFunction = this.props.detailsPane ? this._onFocusPasswordFieldDetails : this._onFocusPasswordField;
        let passwordFieldAriaLabel = this._getPasswordFieldAriaLabel();
        const iconProps: IIconProps = (this.state.displayRemovePasswordButton) ?
            {
                iconName: 'Cancel',
                onClick: this._onDeletePassword,
                className: 'od-PasswordField-delete',
                ariaLabel: stringsResx.removePassword,
                tabIndex: 0,
            } : undefined;
        return (
            <div className='od-PasswordField'>
                <div className='od-PasswordField-passwordFieldLine'>
                    <div className='od-PasswordField-label'>
                        <Icon iconName='Lock' />
                    </div>
                    { this._renderPremiumGleam() }
                    <div className='od-ShareLink-passwordFieldContent'>
                        <TextField
                            type='password'
                            autoComplete='off'
                            disabled={ this.state.disablePasswordField }
                            placeholder={ stringsResx.setPasswordFieldText }
                            value={ this.state.currentPassword }
                            onChanged={ this._onUpdatePassword }
                            onFocus={ focusPasswordFieldFunction }
                            onBlur={ this._onBlurPasswordField }
                            borderless
                            ariaLabel={ passwordFieldAriaLabel }
                            iconProps={ iconProps }
                            readOnly={ this.props.readOnly }
                            tabIndex={ this.props.tabIndex }
                        />
                    </div>
                </div>
                { this._renderShareReminder() }
            </div>
        );
    }

    /**
     * May become password field in the future
     */
    private _renderShareReminder() {
        let shareReminderDivName = 'od-PasswordField-shareReminder';
        if (this.props.detailsPane) {
            // CSS Formating is slightly different for detailsPane
            shareReminderDivName += '-detailsPane';
        }
        if (this.state.displayNotificationText) {
            if (this.state.hasExistingPassword) {
                if (this.props.detailsPane) {
                    return (
                        <div className='odPasswordField-lastModifiedBy'>
                            <div className={ shareReminderDivName } >
                                { stringsResx.passwordFieldPasswordSetBy + this.props.lastModifiedBy }
                            </div>
                        </div>
                    );
                }
            } else {
                const showPasswordStrengthMeter = FeatureOverrides.isFeatureEnabled(PasswordStrengthMeterUI);
                if (showPasswordStrengthMeter && this.state.currentPassword.length > 0) {
                    return (
                        <div className={ shareReminderDivName } >
                            <PasswordStrengthMeter
                                currentPassword={ this.state.currentPassword }
                                getStrengthMeterValue={ this._getStrengthMeterValue }
                            />
                        </div>
                    );
                } else {
                    return (
                        <div className={ shareReminderDivName } >
                            { stringsResx.passwordFieldShareReminder }
                        </div>
                    );
                }
            }
        } else {
            return (
                <div className={ shareReminderDivName } >
                    { '' }
                </div>
            );
        }
    }

    private _renderPremiumGleam() {
        if (this.props.showGleam) {
            return <Icon role='presentation' iconName='StarburstSolid' className='od-PasswordField-premiumStarburst' />;
        } else {
            return;
        }
    }

    /**
     * When we focus on the Password Field we want to clear the current password for the user to type
     */
    private _onFocusPasswordFieldDetails() {
        this.setState({
            ...this.state,
            currentPassword: '',
            displayNotificationText: true,
            displayRemovePasswordButton: false,
            hasExistingPassword: false
        }, () => {
            this.props.onPasswordChange('');
        });
    }

    /**
     * In share we do not want to wipe the password when clicking the password field
     */
    private _onFocusPasswordField() {
        this.setState({
            ...this.state,
            displayNotificationText: true
        });
    }

    /**
     * When we blur the password field, we check if there's already a password in there. We want the strength meter
     * to stay persistent even when we blur, but if there's no password we don't want the text below to display.
     */
    private _onBlurPasswordField() {
        const showPasswordStrengthMeter = FeatureOverrides.isFeatureEnabled(PasswordStrengthMeterUI);
        if (!(showPasswordStrengthMeter && this.state.currentPassword.length > 0)) {
            this.setState({
                ...this.state,
                displayNotificationText: false
            });
        }
    }

    /**
     * For manage access, when we have a text in the password field, display the remove password button.
     * @param newPassword
     */
    private _onUpdatePassword(newPassword: string) {
        const isNotEmptyString = newPassword.length > 0;
        this.setState({
            ...this.state,
            currentPassword: newPassword,
            displayRemovePasswordButton: isNotEmptyString && this.props.detailsPane
        }, () => {
            this.props.onPasswordChange(newPassword);
        });
    }

    /**
     * When we delete a preexisting password, remove the displayRemovePasswordButton
     */
    private _onDeletePassword(): void {
        this.setState({
            ...this.state,
            currentPassword: '',
            disablePasswordField: false,
            displayRemovePasswordButton: false,
            hasExistingPassword: false
        }, () => {
            this.props.onPasswordChange('');
        });
    }

    private _getPasswordFieldAriaLabel() {
        const showPasswordStrengthMeter = FeatureOverrides.isFeatureEnabled(PasswordStrengthMeterUI);
        if (showPasswordStrengthMeter && this.state.currentPassword.length > 0) {
            return StringHelper.format(stringsResx.currentStrength, this._currentStrengthMeterValue)
        } else {
            return stringsResx.passwordFieldName;
        }
    }

    private _getStrengthMeterValue(passwordStrengthValue: string) {
        this._currentStrengthMeterValue = passwordStrengthValue;
    }
}
